/**
 * Processes text content by converting line breaks to HTML if it's plain text
 * @param content - The text content to process
 * @returns Processed content with proper HTML formatting
 */
export function processTextContent(content: string): string {
  if (!content) return ''
  
  // Check if content already contains HTML tags
  const hasHtml = /<[a-z][\s\S]*>/i.test(content)
  
  // If it's already HTML, return as-is
  if (hasHtml) {
    return content
  }
  
  // Convert line breaks to <br> tags for plain text
  let processedContent = content.replace(/\r?\n/g, '<br>')
  
  // Split by double line breaks and wrap each part in <p> tags for better spacing
  const paragraphs = processedContent.split(/<br\s*\/?>\s*<br\s*\/?>/i)
  
  if (paragraphs.length > 1) {
    // Multiple paragraphs detected
    processedContent = paragraphs
      .map(p => p.trim())
      .filter(p => p.length > 0)
      .map(p => `<p>${p}</p>`)
      .join('')
  } else if (processedContent.length > 0) {
    // Single paragraph, wrap it unless it's just line breaks
    if (processedContent.replace(/<br\s*\/?>/gi, '').trim().length > 0) {
      processedContent = `<p>${processedContent}</p>`
    }
  }
  
  return processedContent
}

/**
 * Converts HTML back to plain text for editing
 * @param htmlContent - The HTML content to convert
 * @returns Plain text with line breaks
 */
export function htmlToPlainText(htmlContent: string): string {
  if (!htmlContent) return ''
  
  return htmlContent
    // Convert </p><p> to double line breaks
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
    // Convert <br> tags to single line breaks
    .replace(/<br\s*\/?>/gi, '\n')
    // Remove remaining HTML tags
    .replace(/<[^>]*>/g, '')
    // Decode HTML entities
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    // Clean up extra whitespace
    .trim()
}